import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import { AnalyticsHeader } from './components/AnalyticsHeader';
import { PeriodSelector } from './components/PeriodSelector';
import { SiteFilter } from './components/SiteFilter';
import { KpiCards } from './components/KpiCards';
import { UptimeDonut } from './components/UptimeDonut';
import { SlaGauge } from './components/SlaGauge';
import { UptimeChart } from './components/UptimeChart';
import { ResponseTimeChart } from './components/ResponseTimeChart';
import { IncidentsBarChart } from './components/IncidentsBarChart';
import { HeatmapGrid } from './components/HeatmapGrid';
import { SiteRankingTable } from './components/SiteRankingTable';
import { ExportButton } from './components/ExportButton';
import { AnalyticsSkeleton } from './components/AnalyticsSkeleton';
import { EmptyAnalytics } from './components/EmptyAnalytics';
import { DateRangePicker } from './components/DateRangePicker';

import { useAnalytics } from './hooks/useAnalytics';
import { useAnalyticsFilters } from './hooks/useAnalyticsFilters';
import type { TimePeriod } from './types';

export const AnalyticsPage: React.FC = () => {
    const [showDatePicker, setShowDatePicker] = useState(false);

    const {
        period,
        customStart,
        customEnd,
        selectedSites,
        compareWithPrevious,
        setPeriod,
        setCustomRange,
        toggleSite,
        selectAllSites,
        clearSites,
        setCompare,
        periodLabel,
    } = useAnalyticsFilters();

    const filters = {
        period,
        customStart,
        customEnd,
        selectedSites,
        compareWithPrevious,
    };

    const {
        summary,
        sites,
        timeSeries,
        heatmaps,
        isLoading,
        error,
        refresh,
        lastUpdateText,
        isStale,
    } = useAnalytics(filters);

    const handleRefresh = useCallback(async () => {
        await refresh();
    }, [refresh]);

    const handlePeriodChange = useCallback(
        (p: TimePeriod) => {
            if (p === 'custom') {
                setShowDatePicker(true);
            } else {
                setPeriod(p);
                setShowDatePicker(false);
            }
        },
        [setPeriod],
    );

    const handleCustomRange = useCallback(
        (start: string, end: string) => {
            setCustomRange(start, end);
            setShowDatePicker(false);
        },
        [setCustomRange],
    );

    const handleSelectAll = useCallback(() => {
        if (sites.length > 0) {
            selectAllSites(sites.map(s => s.siteId));
        } else {
            clearSites();
        }
    }, [selectAllSites, clearSites, sites]);

    const dataPoints = timeSeries.reduce((s, t) => s + t.data.length, 0);

    if (error) {
        return (
            <div className="min-h-screen pb-20 md:pb-0">
                <AnalyticsHeader
                    summary={undefined}
                    periodLabel={periodLabel}
                    isLoading={false}
                    isStale={true}
                    onRefresh={handleRefresh}
                    lastUpdate={null}
                />
                <EmptyAnalytics
                    type="error"
                    errorMessage={error instanceof Error ? error.message : 'Неизвестная ошибка'}
                    onRetry={handleRefresh}
                />
            </div>
        );
    }

    if (isLoading && !summary) {
        return <AnalyticsSkeleton />;
    }

    const hasData = sites.length > 0 && timeSeries.length > 0;

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="min-h-screen pb-20 md:pb-0"
        >
            <AnalyticsHeader
                summary={summary}
                periodLabel={periodLabel}
                isLoading={isLoading}
                isStale={isStale}
                onRefresh={handleRefresh}
                lastUpdate={lastUpdateText}
            />

            <div className="px-[18px] pb-4 flex items-center gap-2 flex-wrap relative">
                <div className="relative">
                    <PeriodSelector value={period} onChange={handlePeriodChange} />

                    <AnimatePresence>
                        {showDatePicker && (
                            <DateRangePicker
                                startDate={customStart}
                                endDate={customEnd}
                                onChange={handleCustomRange}
                                onClose={() => setShowDatePicker(false)}
                            />
                        )}
                    </AnimatePresence>
                </div>

                {sites.length > 0 && (
                    <SiteFilter
                        sites={sites}
                        selectedSites={selectedSites}
                        onToggle={toggleSite}
                        onSelectAll={handleSelectAll}
                        onClearAll={clearSites}
                    />
                )}

                <motion.button
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    onClick={() => setCompare(!compareWithPrevious)}
                    className={`
                        flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-[12px] font-medium transition-all
                        ${compareWithPrevious
                            ? 'bg-[var(--brand-dim)] border-[var(--brand-border)] text-[var(--brand)]'
                            : 'bg-[var(--bg-elevated)] border-[var(--border)] text-[var(--text-secondary)] hover:border-[var(--border-strong)] hover:text-[var(--text)]'
                        }
                    `}
                    aria-pressed={compareWithPrevious}
                >
                    <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                    </svg>
                    Сравнить
                </motion.button>

                <div className="ml-auto">
                    <ExportButton period={period} siteIds={selectedSites} />
                </div>
            </div>

            <AnimatePresence mode="wait">
                {!hasData && !isLoading ? (
                    <motion.div
                        key="empty"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <EmptyAnalytics
                            type={selectedSites.length > 0 ? 'filtered-empty' : 'no-data'}
                            onReset={selectedSites.length > 0 ? clearSites : undefined}
                            onRetry={handleRefresh}
                        />
                    </motion.div>
                ) : (
                    <motion.div
                        key="content"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <KpiCards
                            summary={summary}
                            timeSeries={timeSeries}
                            isLoading={isLoading}
                        />

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 px-[18px] pb-3">
                            <UptimeDonut
                                summary={summary}
                                sites={sites}
                                isLoading={isLoading}
                            />
                            <SlaGauge
                                summary={summary}
                                isLoading={isLoading}
                            />
                        </div>

                        <div className="pb-3">
                            <UptimeChart timeSeries={timeSeries} isLoading={isLoading} />
                        </div>
                        <div className="pb-3">
                            <ResponseTimeChart timeSeries={timeSeries} isLoading={isLoading} />
                        </div>
                        <div className="pb-3">
                            <IncidentsBarChart
                                timeSeries={timeSeries}
                                sites={sites}
                                isLoading={isLoading}
                            />
                        </div>

                        <div className="pb-3">
                            <HeatmapGrid heatmaps={heatmaps} isLoading={isLoading} />
                        </div>

                        <div className="pb-4">
                            <SiteRankingTable sites={sites} isLoading={isLoading} />
                        </div>

                        <div className="px-[18px] pt-4 pb-6 border-t border-[var(--border)]">
                            <div className="flex items-center justify-between text-[11px] text-[var(--text-muted)] font-mono">
                                <span>
                                    {sites.length} {sites.length === 1 ? 'сайт' : sites.length < 5 ? 'сайта' : 'сайтов'}
                                    {' · '}{dataPoints} точек данных
                                </span>
                                <span>Автообновление каждые 30 сек</span>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};